import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { avatarColor } from "./cells";
import { Settings } from "./Settings";
import { AdminPanel } from "./AdminPanel";
import { ItemDrawer } from "./ItemDrawer";
import { TableView } from "./views/TableView";
import { KanbanView } from "./views/KanbanView";
import { GanttView } from "./views/GanttView";
import { CalendarView } from "./views/CalendarView";
import type { BoardSnapshot, BoardSummary, CellValue, Item, User } from "./types";

type ViewName = "table" | "kanban" | "gantt" | "calendar";
const VIEWS: Array<{ key: ViewName; label: string }> = [
  { key: "table", label: "Hauptansicht" },
  { key: "kanban", label: "Kanban" },
  { key: "calendar", label: "Kalender" },
  { key: "gantt", label: "Gantt" },
];

export function Board({ user, onLogout, onUserChange }: {
  user: User; onLogout: () => void; onUserChange: (u: User) => void;
}) {
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [activeBoardId, setActiveBoardId] = useState<string | null>(null);
  const [snap, setSnap] = useState<BoardSnapshot | null>(null);
  const [view, setView] = useState<ViewName>("table");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [drawerItem, setDrawerItem] = useState<Item | null>(null);
  const [updateCounts, setUpdateCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  // table toolbar state
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);

  const loadBoards = useCallback(async () => {
    const r = await api.boards();
    setBoards(r.boards);
    setActiveBoardId((cur) => cur ?? r.boards[0]?.id ?? null);
    return r.boards;
  }, []);

  const reload = useCallback(async () => {
    if (!activeBoardId) return;
    const [s, counts] = await Promise.all([api.board(activeBoardId), api.updateCounts(activeBoardId).catch(() => ({ counts: {} }))]);
    setSnap(s);
    setUpdateCounts(counts.counts);
  }, [activeBoardId]);

  useEffect(() => { loadBoards().finally(() => setLoading(false)); }, [loadBoards]);
  useEffect(() => { if (activeBoardId) { setLoading(true); reload().finally(() => setLoading(false)); } }, [activeBoardId, reload]);

  const patchItemCell = useCallback((itemId: string, colId: string, value: CellValue) => {
    setSnap((prev) => prev && {
      ...prev,
      items: prev.items.map((it) => it.id === itemId
        ? { ...it, cells: { ...it.cells, [colId]: value }, updated_at: Date.now() } : it),
    });
    api.setCell(itemId, colId, value).catch(() => reload());
  }, [reload]);

  async function logout() { await api.logout(); onLogout(); }
  async function onImported(boardId: string) { await loadBoards(); setActiveBoardId(boardId); setSettingsOpen(false); }

  async function createBoard() {
    const name = prompt("Name des neuen Boards:", "Neues Board");
    if (!name) return;
    const r = await api.createBoard(name);
    await loadBoards();
    setSnap(null);
    setActiveBoardId(r.board.id);
  }

  async function deleteBoard() {
    if (!snap) return;
    if (!confirm(`Board „${snap.board.name}" wirklich löschen? Alle Projekte, Dateien und Updates gehen verloren.`)) return;
    await api.deleteBoard(snap.board.id);
    const remaining = await loadBoards();
    setSnap(null);
    setActiveBoardId(remaining.find((b) => b.id !== snap.board.id)?.id ?? null);
  }

  async function renameBoard() {
    if (!activeBoardId) return;
    const n = prompt("Board umbenennen:", activeBoard?.name ?? "");
    if (!n || !n.trim()) return;
    await api.renameBoard(activeBoardId, n.trim());
    await loadBoards();
    setSnap((prev) => prev && { ...prev, board: { ...prev.board, name: n.trim() } });
  }

  async function addGroup() {
    if (!snap) return;
    await api.addGroup(snap.board.id, { title: "Neue Gruppe" });
    reload();
  }

  const activeBoard = boards.find((b) => b.id === activeBoardId);
  const canManageBoard = user.isAdmin || (activeBoard?.role === "owner");

  return (
    <div className="shell">
      <div className="topbar">
        <div className="brand">
          <div className="brand-dot" style={{ width: 26, height: 26, fontSize: 13 }}>T</div>
          <span className="brand-name" style={{ fontSize: 15 }}>TACTYC</span>
        </div>
        <select className="input" style={{ width: "auto", padding: "5px 8px", background: "var(--bg-2)" }}
          value={activeBoardId ?? ""} onChange={(e) => { setActiveBoardId(e.target.value); setSnap(null); }}>
          {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        {canManageBoard && (
          <button className="btn btn-ghost btn-sm" onClick={renameBoard} title="Board umbenennen">✎</button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={createBoard} title="Neues Board">＋ Board</button>
        {canManageBoard && boards.length > 1 && (
          <button className="btn btn-ghost btn-sm" onClick={deleteBoard} title="Board löschen">🗑</button>
        )}
        <span className="spacer" />
        {user.isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => setAdminOpen(true)}>🛡 Admin</button>}
        <button className="btn btn-ghost btn-sm" onClick={() => setSettingsOpen(true)}>⚙ Einstellungen & Import</button>
        <span className="avatar" style={{ background: avatarColor(user.name || user.email), width: 30, height: 30 }} title={user.email}>
          {(user.name || user.email).slice(0, 2).toUpperCase()}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={logout}>Abmelden</button>
      </div>

      <div className="tabs">
        {VIEWS.map((v) => (
          <button key={v.key} className={"tab" + (view === v.key ? " active" : "")} onClick={() => setView(v.key)}>{v.label}</button>
        ))}
      </div>

      {view === "table" && snap && (
        <div className="toolbar">
          <input className="input" style={{ width: 220, padding: "6px 10px" }} placeholder="🔍 Suchen…"
            value={query} onChange={(e) => setQuery(e.target.value)} />
          <select className="input" style={{ width: "auto", padding: "6px 8px" }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="">Sortierung: Manuell</option>
            <option value="name">Name A–Z</option>
            <option value="name_desc">Name Z–A</option>
            <option value="updated">Zuletzt geändert</option>
          </select>
          <button className={"btn btn-sm " + (onlyMine ? "" : "btn-ghost")} onClick={() => setOnlyMine((v) => !v)}
            title="Nur Projekte, in denen ich zugewiesen bin">👤 Nur meine</button>
          <span className="spacer" />
          {canManageBoard && <button className="btn btn-ghost btn-sm" onClick={addGroup}>＋ Neue Gruppe</button>}
        </div>
      )}

      {loading || !snap ? (
        <div className="center-screen"><div className="spin" /></div>
      ) : view === "table" ? (
        <TableView snap={snap} reload={reload} patchItemCell={patchItemCell}
          updateCounts={updateCounts} onOpenItem={setDrawerItem} isAdmin={user.isAdmin}
          canManage={canManageBoard} query={query} sort={sort} onlyMine={onlyMine}
          currentUser={{ id: user.id, name: user.name }} />
      ) : view === "kanban" ? (
        <KanbanView snap={snap} reload={reload} />
      ) : view === "gantt" ? (
        <GanttView snap={snap} />
      ) : (
        <CalendarView snap={snap} />
      )}

      {settingsOpen && <Settings user={user} onClose={() => setSettingsOpen(false)} onUserChange={onUserChange} onImported={onImported} />}
      {adminOpen && <AdminPanel meId={user.id} onClose={() => setAdminOpen(false)} />}
      {drawerItem && (
        <ItemDrawer item={drawerItem} user={user} onClose={() => setDrawerItem(null)}
          onCountChange={(id, n) => setUpdateCounts((c) => ({ ...c, [id]: n }))} />
      )}
    </div>
  );
}
